class BundleBuilder extends HTMLElement {
  constructor() {
    super();
    this.flavourCounter = 0;
    this.limits = this.getLimits();
    this.flavourQtyItems = null;
    this.userSelectedBundle = false;
    this.selectedLimit = null;
  }

  connectedCallback() {
    this.initBundleButtons();
    this.initQuantitySelectors();
    this.initFrecuencySelectors();
    this.initAddToCart();
    this.updateBundleSelectors();
  }

  getGuid() {
    let d = new Date().getTime();
    let d2 = (performance && performance.now && performance.now() * 1000) || 0;
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      let r = Math.random() * 16;
      if (d > 0) {
        r = (d + r) % 16 | 0;
        d = Math.floor(d / 16);
      } else {
        r = (d2 + r) % 16 | 0;
        d2 = Math.floor(d2 / 16);
      }
      return (c == "x" ? r : (r & 0x7) | 0x8).toString(16);
    });
  }

  getLimits() {
    const bundleForms = [...this.querySelectorAll(".form-selector")];

    return bundleForms
      .map((form) => Number(form.dataset.limit || 0))
      .filter((n) => !Number.isNaN(n))
      .sort((a, b) => a - b);
  }

  updateBundleSelectors() {
    const total = this.flavourCounter;
    const limits = this.limits;

    if (!limits.length) return;

    const maxLimit = Number(limits[limits.length - 1]);

    let derivedLimit = maxLimit;
    for (const rawLimit of limits) {
      const limit = Number(rawLimit);
      if (total <= limit) {
        derivedLimit = limit;
        break;
      }
    }

    if (typeof this.selectedLimit !== "number") {
      this.selectedLimit = derivedLimit;
    }

    let currentLimit;
    if (this.userSelectedBundle) {
      if (total > this.selectedLimit) {
        this.selectedLimit = derivedLimit;
      }
      currentLimit = this.selectedLimit;
    } else {
      currentLimit = derivedLimit;
    }

    this.currentLimit = currentLimit;

    const bundleItems = [
      ...this.querySelectorAll(".custom-bundles-wrapper .custom-bundle-item"),
    ];

    bundleItems.forEach((item) => {
      const qty = Number(item.dataset.quantity || "0");
      item.classList.toggle("custom-bundle-item-active", qty === currentLimit);
    });

    const bundleForms = [...this.querySelectorAll(".form-selector")];

    bundleForms.forEach((form) => {
      const limit = Number(form.dataset.limit || "0");
      form.classList.toggle("hidden", limit !== currentLimit);
    });

    const disablePlus = total >= maxLimit;

    const plusButtons = [
      ...this.querySelectorAll(".flavour-container .flavour-plus"),
    ];

    plusButtons.forEach((btn) => {
      btn.disabled = disablePlus;
      btn.classList.toggle("flavour-plus--disabled", disablePlus);
    });

    const container = this.querySelector(".form-selector-container");
    if (!container) return;

    const activeFormSelector =
      container.querySelector(".form-selector:not(.hidden)") ||
      container.querySelector(".form-selector");

    if (!activeFormSelector) return;

    const atcButton = activeFormSelector.querySelector(
      ".lsg-bundle-submit-button"
    );
    const packItemsSpan = activeFormSelector.querySelector(
      "[data-bundle-pack-items]"
    );
    const atcTextWrapper = activeFormSelector.querySelector(
      "[data-bundle-atc-text]"
    );
    const addMoreTextWrapper = activeFormSelector.querySelector(
      "[data-bundle-atc-more-text]"
    );

    const currentLimitNum = Number(activeFormSelector.dataset.limit || "0");
    const missingQty = Math.max(currentLimitNum - total, 0);
    const isComplete = currentLimitNum > 0 && total >= currentLimitNum;

    if (packItemsSpan) {
      packItemsSpan.textContent = missingQty;
    }

    if (atcButton) {
      atcButton.disabled = !isComplete;
      atcButton.classList[isComplete ? "remove" : "add"]("disabled");
    }

    if (atcTextWrapper && addMoreTextWrapper) {
      atcTextWrapper.style.display = isComplete ? "" : "none";
      addMoreTextWrapper.style.display = isComplete ? "none" : "";
    }
  }

  initQuantitySelectors() {
    if (!this.flavourQtyItems) {
      this.flavourQtyItems = [
        ...this.querySelectorAll(
          ".flavour-container .flavour-quantity-selector"
        ),
      ];
    }

    const flavourQtyItems = this.flavourQtyItems;

    const updateQty = (currentQtyItem, delta) => {
      if (!currentQtyItem) return;

      const raw = parseInt(currentQtyItem.value || "0", 10);
      const current = Number.isNaN(raw) ? 0 : raw;

      const newQty = Math.max(current + delta, 0);
      const realDelta = newQty - current;

      if (realDelta === 0) return;

      currentQtyItem.value = newQty;
      this.flavourCounter = Math.max(this.flavourCounter + realDelta, 0);

      this.updateBundleSelectors();
    };

    flavourQtyItems.forEach((item) => {
      const plusButtonItem = item.querySelector(".flavour-plus");
      const minusButtonItem = item.querySelector(".flavour-minus");
      const currentQtyItem = item.querySelector('input[name="quantity"]');

      plusButtonItem?.addEventListener("click", () =>
        updateQty(currentQtyItem, +1)
      );

      minusButtonItem?.addEventListener("click", () =>
        updateQty(currentQtyItem, -1)
      );
    });
  }

  initFrecuencySelectors() {
    const frecuencySelectors = [...this.querySelectorAll(".bundle-option")];

    frecuencySelectors.forEach((selector) => {
      selector.addEventListener("click", () => {
        const container = selector.closest(".form-selector");
        if (!container) return;

        const siblings = [...container.querySelectorAll(".bundle-option")];
        siblings.forEach((el) => el.classList.remove("bundle-option-selected"));
        selector.classList.add("bundle-option-selected");
        const priceElement = selector.querySelector(".pack-price");
        const addToCartPrice = container.querySelector(
          ".lsg-bundle-submit-button [data-bundle-total]"
        );

        if (!priceElement || !addToCartPrice) return;

        const priceText = priceElement.textContent.trim();

        addToCartPrice.textContent = priceText;
      });
    });
  }

  initBundleButtons() {
    const bundleButtons = [
      ...this.querySelectorAll(".custom-bundles-wrapper .custom-bundle-item"),
    ];
    if (!bundleButtons.length) return;

    bundleButtons.forEach((btn) => {
      btn.addEventListener("click", () => {
        const qty = Number(btn.dataset.quantity || "0");
        if (!qty) return;

        const prevSelected =
          typeof this.selectedLimit === "number" ? this.selectedLimit : null;
        const isDowngrade = prevSelected !== null && qty < prevSelected;

        this.userSelectedBundle = true;
        this.selectedLimit = qty;

        if (isDowngrade) {
          const flavourInputs = [
            ...this.querySelectorAll(
              ".custom-bundle-flavour-items .flavour-container input[name='quantity']"
            ),
          ];
          flavourInputs.forEach((input) => {
            input.value = "0";
          });
          this.flavourCounter = 0;
        }

        this.updateBundleSelectors();
      });
    });
  }

  initAddToCart() {
    const forms = [
      ...this.querySelectorAll(".form-selector-container .lsg-bundle-form"),
    ];

    forms.forEach((form) => {
      form.addEventListener("click", (evt) => {
        evt.preventDefault();

        const container = form.closest(".form-selector-container");
        if (!container) {
          console.warn("No form-selector-container for form", form);
          return;
        }
        let formSelector =
          container.querySelector(".form-selector:not(.hidden)") ||
          container.querySelector(".form-selector");

        if (!formSelector) {
          console.warn("No form-selector found in container", container);
          return;
        }
        const bundleLimit = parseInt(formSelector.dataset.limit || "0", 10);

        const currentFrecuency = formSelector.querySelector(
          ".bundle-option.bundle-option-selected"
        );
        const getCurrentOption = currentFrecuency?.dataset.option;

        let sellingPlanId = null;

        if (getCurrentOption === "subscription") {
          const sellingPlanSelect = formSelector.querySelector(
            ".lsg-bundle-interval-frequency-select"
          );
          sellingPlanId = sellingPlanSelect?.value || null;
        }

        const flavoursItems = [
          ...this.querySelectorAll(
            ".custom-bundle-flavour-items .flavour-container"
          ),
        ];
        const bundleID = this.getGuid();

        const items = flavoursItems
          .filter((item) => {
            const input = item.querySelector('input[name="quantity"]');
            const qty = parseInt(input?.value || "0", 10);
            return qty > 0;
          })
          .map((item) => {
            const input = item.querySelector('input[name="quantity"]');
            const qty = parseInt(input?.value || "0", 10);
            const baseItem = {
              id: item.dataset.variantId,
              quantity: qty,
              properties: {
                _bundle_id: bundleID,
              },
            };

            if (getCurrentOption === "subscription" && sellingPlanId) {
              baseItem.selling_plan = sellingPlanId;
            }

            return baseItem;
          });

        items.push({
          id: currentFrecuency.dataset.subscriptionVariantId,
          quantity: 1,
          ...(sellingPlanId && { selling_plan: sellingPlanId }),
          properties: {
            _bundle_id: bundleID,
            _bundle_parent: true,
            _bundle_max: bundleLimit,
            _bundle_min: bundleLimit,
            _bundle_discount_code: "",
          },
        });

        fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({ items }),
          credentials: "same-origin",
        })
          .then((res) => res.json())
          .then(() => {
            setTimeout(() => {
              if (window.Rebuy?.SmartCart?.show) {
                window.Rebuy.SmartCart.show();
              }
            }, 2000);
          })
          .catch((error) => {
            console.error(error);
          });
      });
    });
  }
}

customElements.define("custom-builder", BundleBuilder);